import { useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, makeStyles } from '@material-ui/core';

import { DeliveryPoint } from '../../../delivery-points/types/delivery-point.type';
import { DeliveryPointSearchTable } from '../../../delivery-points/components/Tables/DeliveryPointSearchTable.component';
import pluralFormatter from '../../../shared/utils/plural-formater';
import { useUpdateOrganizationDeliveryPoints } from '../../hooks/update-organization-delivery-points.hook';

type Props = {
  actionLabel: string;
  organizationId: string;
  currentDeliveryPointIds: string[];
  refreshOrganization: () => Promise<void>;
};

const useStyles = makeStyles((theme) => ({
  root: {
    '& >.MuiDialog-container': {
      '& > .MuiPaper-root': {
        minWidth: '65svw',
        padding: '1rem 0.75rem',
      },
    },
  },
  content: {
    padding: '0.5rem',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',

    '& > p': {
      margin: 0,
    },

    '& > section': {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
  },
  ctaButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export default function AddDeliveryPointsDialog({
  actionLabel,
  organizationId,
  currentDeliveryPointIds,
  refreshOrganization,
}: Props) {
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [deliveryPointsToAdd, setDeliveryPointsToAdd] = useState<DeliveryPoint[]>([]);

  const { updateOrganizationDeliveryPoints, isUpdating } = useUpdateOrganizationDeliveryPoints();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleAddDeliveryPoint = (deliveryPoint: DeliveryPoint) => {
    setDeliveryPointsToAdd((previouslySelected) =>
      previouslySelected.find((selectedDeliveryPoint) => selectedDeliveryPoint.id === deliveryPoint.id)
        ? previouslySelected
        : [...previouslySelected, deliveryPoint]
    );
  };

  const handleCancel = () => {
    setDeliveryPointsToAdd([]);
    handleClose();
  };

  const handleSubmit = async () => {
    const updatedOrganization = await updateOrganizationDeliveryPoints({
      organizationId,
      deliveryPointIds: [...currentDeliveryPointIds, ...deliveryPointsToAdd.map((deliveryPoint) => deliveryPoint.id)],
    });

    setDeliveryPointsToAdd([]);
    handleClose();

    if (updatedOrganization) await refreshOrganization();
  };

  const selectedDeliveryPointIds = [
    ...currentDeliveryPointIds,
    ...deliveryPointsToAdd.map((deliveryPoint) => deliveryPoint.id),
  ];

  return (
    <>
      <Button size="small" variant="contained" className={classes.ctaButton} onClick={handleOpen}>
        {actionLabel}
      </Button>
      <Dialog open={open} onClose={handleClose} className={classes.root}>
        <section className={classes.content}>
          <DeliveryPointSearchTable
            selectedDeliveryPointIds={selectedDeliveryPointIds}
            onAddDeliveryPoint={handleAddDeliveryPoint}
            maxHeight="40svh"
          />
        </section>
        <DialogActions className={classes.actions}>
          <p>
            {deliveryPointsToAdd.length > 0
              ? `${deliveryPointsToAdd.length} ${pluralFormatter({ count: deliveryPointsToAdd.length, singular: 'point de livraison sélectionné', plural: 'points de livraison sélectionnés' })}`
              : 'Aucun point de livraison sélectionné'}
          </p>
          <section>
            <Button onClick={handleCancel} disabled={isUpdating}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={!deliveryPointsToAdd.length || isUpdating}
            >
              {isUpdating ? <CircularProgress size={16} /> : 'Ajouter'}
            </Button>
          </section>
        </DialogActions>
      </Dialog>
    </>
  );
}
